import { effect, inject, Injectable, NgZone } from '@angular/core';
import { DEFAULT_IDLE_TIME } from '@core/models/settings';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@shared/services/notification.service';
import { fromEvent, merge, startWith, Subscription, switchMap, throttleTime, timer } from 'rxjs';
import { AuthentificationService } from '../authentification/authentification.service';
import { AppSettingsService } from '../settings/settings.service';
import { UserService } from '../user/user.service';

/**
 * Déconnecte automatiquement l'utilisateur après un délai d'inactivité, sans aucune activité
 * (souris, clavier, scroll, tactile). Le suivi ne démarre que si l'utilisateur est connecté, et
 * s'arrête à la déconnexion.
 */
@Injectable({ providedIn: 'root' })
export class InactivityService {
  private readonly authService = inject(AuthentificationService);
  private readonly userService = inject(UserService);
  private readonly notificationService = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly zone = inject(NgZone);
  private readonly appSettingsService = inject(AppSettingsService);

  /** Délai d'inactivité (en minutes) avant déconnexion automatique. */
  private readonly INACTIVITY_TIMEOUT_MINUTES = Math.max(
    this.appSettingsService.settings().idleMinutesTime || 0,
    DEFAULT_IDLE_TIME,
  );

  /** Délai d'inactivité avant déconnexion automatique. */
  private readonly INACTIVITY_TIMEOUT_MS = this.INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;

  /** On ne relance le minuteur qu'une fois par seconde max, pour ne pas réagir à chaque mousemove. */
  private readonly ACTIVITY_THROTTLE_MS = 1000;

  private readonly ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

  private idleSubscription?: Subscription;

  constructor() {
    effect(() => (this.authService.isLoggedIn() ? this.start() : this.stop()));
  }

  private start(): void {
    if (this.idleSubscription) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      const activity$ = merge(...this.ACTIVITY_EVENTS.map((event) => fromEvent(document, event, { passive: true })));

      this.idleSubscription = activity$
        .pipe(
          throttleTime(this.ACTIVITY_THROTTLE_MS),
          startWith(0),
          switchMap(() => timer(this.INACTIVITY_TIMEOUT_MS)),
        )
        .subscribe(() => this.zone.run(() => this.onTimeout()));
    });
  }

  private stop(): void {
    this.idleSubscription?.unsubscribe();
    this.idleSubscription = undefined;
  }

  private onTimeout(): void {
    this.stop();
    const message = this.translate.instant('core.session-timeout', { minutes: this.INACTIVITY_TIMEOUT_MINUTES });
    this.notificationService.info(message);
    this.userService.doLogout();
  }
}
